import { useState } from 'react'

import { Button, useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { CompetenciesDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  CompetenciesDeleteCompetencyDocument,
  CompetenciesDuplicateCompetencyDocument,
  CompetenciesTableItemFragment,
} from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EditCompetencyModal } from 'modules/competencies/components/EditCompetencyModal'

interface Props {
  competency: CompetenciesTableItemFragment
  isCompetenciesRevampEnabled: boolean
}

export const CompetencyItemActions = ({ competency, isCompetenciesRevampEnabled }: Props) => {
  const { navigation } = useNavigation()
  const { openDialog } = useConfirmationDialog()
  const [showEditCompetencyModal, setShowEditCompetencyModal] = useState(false)

  const [duplicateCompetency, { loading: duplicating }] = useMutation(
    CompetenciesDuplicateCompetencyDocument,
    {
      extractorFn: (data) => data.competenciesMutations.duplicateCompetenciesCompetency,
    }
  )
  const [deleteCompetenciesCompetency, { loading: deleting }] = useMutation(
    CompetenciesDeleteCompetencyDocument,
    {
      extractorFn: (data) => data.competenciesMutations.deleteCompetenciesCompetency,
    }
  )

  const onDuplicate = async () => {
    const result = await duplicateCompetency({
      variables: { id: competency.id },
      refetchQueries: [CompetenciesDataTableDocument],
    })

    const handled = handleResult(result, () => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('competencies_v3.competencies_table.actions.duplicate.success'),
      })
      track(trackingEvents.competencies.duplicateCompetency)
    }

    return handled
  }

  const onDelete = async () => {
    const result = await deleteCompetenciesCompetency({
      variables: { id: competency.id },
      update(cache) {
        cache.evict({
          id: cache.identify({ id: competency.id, __typename: 'CompetenciesCompetency' }),
        })
      },
    })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
      track(trackingEvents.competencies.deleteCompetency)
    }

    return handled
  }

  return (
    <PopoverMenu type="list">
      <PopoverMenu.Trigger asChild>
        <Button
          type="secondary"
          aria-label={i18n.t('competencies_v3.competencies_table.actions.more_options', {
            competencyName: competency.name,
          })}
          iconName="ellipsis"
        />
      </PopoverMenu.Trigger>

      <PopoverMenu.Content side="bottom" align="center">
        {isCompetenciesRevampEnabled ? (
          <PopoverMenu.TouchableOption
            title={i18n.t('competencies_v3.competencies_table.actions.edit')}
            onClick={() => setShowEditCompetencyModal(true)}
          />
        ) : (
          <PopoverMenu.RouterOption
            title={i18n.t('competencies_v3.competencies_table.actions.edit')}
            to={navigation.competencies.competencyId.settings.fullPath({
              competencyId: competency.id,
            })}
          />
        )}
        <PopoverMenu.TouchableOption
          title={i18n.t('competencies_v3.competencies_table.actions.duplicate.label')}
          onClick={onDuplicate}
          disabled={duplicating}
        />
        <PopoverMenu.TouchableOption
          title={i18n.t('competencies_v3.competencies_table.actions.delete')}
          onClick={() =>
            openDialog({
              illustration: 'delete-cross',
              title: i18n.t('competencies_v3.delete_competency.title'),
              description: i18n.t('competencies_v3.delete_competency.description'),
              onConfirm: onDelete,
            })
          }
          disabled={deleting}
          danger
        />
      </PopoverMenu.Content>

      {showEditCompetencyModal && isCompetenciesRevampEnabled && (
        <EditCompetencyModal
          competency={competency}
          onClose={() => setShowEditCompetencyModal(false)}
        />
      )}
    </PopoverMenu>
  )
}
